import { Box, Button, CircularProgress, Grid, Pagination } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import EditColor from "../color/EditColor";
import ActionAreaCard from "../components/ActionAreaCard";
import CustomDialog from "../components/CustomDialog";
import SearchField from "../components/SearchField";
import { useApi } from "../hooks/useApi";
import EditMaterial from "../material/EditMaterial";
import EditModel from "../model/EditModel";
import { API_URL } from "../utils/lib";

export default function ConcreteAssetOverview({ onAdd, onRemove, type, getSelectedAssets }) {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [count, setCount] = useState(0);
  const [query, setQuery] = useState("");
  // state for CustomDialog in which new asset can be created
  const [openAssetCreator, setOpenAssetCreator] = useState(false);
  // state for triggering another fetch of assets
  const [updateCounter, setUpdateCounter] = useState(1);

  const intl = useIntl();
  const api = useApi();

  let creationMessage = "";
  switch (type) {
    case "color":
      creationMessage = intl.formatMessage({ id: "AssetOverview.createColor" });
      break;
    case "material":
      creationMessage = intl.formatMessage({ id: "AssetOverview.createMaterial" });
      break;
    case "model":
      creationMessage = intl.formatMessage({ id: "AssetOverview.createModel" });
      break;
  }

  useEffect(() => {
    setLoading(true);
    api
      .get(`/${type}?page=${page - 1}&size=${pageSize}&search=${query}&assetType=${type}`)
      .then((response) => {
        const selectedAssets = getSelectedAssets();
        setAssets(response.data.content.map(asset => {
          if (selectedAssets.find(a => a.id === asset.id)) {
            return { ...asset, selected: true }
          } else {
            return asset;
          }
        }));
        setCount(response.data.totalPages);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [query, page, pageSize, type, updateCounter]);

  /**
   * Updates selected assets based off current state.
   */
  useEffect(() => {
    const selectedAssets = getSelectedAssets();
    setAssets(oldAssets => oldAssets.map(asset => {
      if (selectedAssets.find(a => a.id === asset.id)) {
        return { ...asset, selected: true }
      } else {
        return { ...asset, selected: false };
      }
    }))
  }, [getSelectedAssets]);

  const clickHandler = (asset) => {
    if (asset.selected) {
      delete asset.selected;
      onRemove(asset);
    } else {
      asset.selected = true;
      onAdd(asset);
    }
  };

  const handleSearch = (value) => {
    setPage(1);
    setQuery(value);
  };

  const handleChange = (event, value) => {
    setPage(value);
  };

  /**
   * Opens the asset-creator-dialog.
   */
  const handleOpenAssetCreator = () => {
    setOpenAssetCreator(true);
  };

  /**
   * Closes the asset-creator-dialog and adds the specified asset
   * to the set of selected assets.
   * Also triggers reload of assets.
   * @param {*} asset 
   */
  const handleCloseAssetCreator = (asset) => {
    // add asset immediately to selected assets
    onAdd(asset);
    setOpenAssetCreator(false);
    // trigger reload of assets
    setUpdateCounter(old => old + 1);
  };

  /**
   * The asset-creator-dialog-content.
   */
  const assetDialog = useMemo(() => {
    if (!openAssetCreator) return;
    switch (type) {
      case "model":
        return <EditModel id={"new"} onClose={handleCloseAssetCreator} />;
      case "material":
        return <EditMaterial id={"new"} onClose={handleCloseAssetCreator} />;
      case "color":
        return <EditColor id={"new"} onClose={handleCloseAssetCreator} />;
      default:
        return;
    }
  }, [openAssetCreator, type, handleCloseAssetCreator]);

  return (
    <Box>
      <CustomDialog
        title={creationMessage}
        content={assetDialog}
        isOpen={openAssetCreator}
        handleAccept={() => { }}
        handleClose={handleCloseAssetCreator}
        maxWidth={"xl"}
        hideConfirmation={true}
        displayClose={true}
      />
      <Button
        variant="contained"
        onClick={() => handleOpenAssetCreator()}
        sx={{ marginBottom: 1 }}
        style={{ float: 'right' }}
      >
        {creationMessage}
      </Button>
      <SearchField onChange={(value) => handleSearch(value)} />

      <Pagination
        size="large"
        count={count}
        page={page}
        sx={{ marginTop: '5px', marginBottom: '5px' }}
        onChange={handleChange}
      />

      <Grid container spacing={3} >
        {assets.map((asset, idx) => {
          const assetType = asset.assetType.toLowerCase();
          return (
            <Grid key={idx} item xs={3}>
              <ActionAreaCard
                title={asset.name}
                description={asset.description}
                src={
                  asset.previewPictureId &&
                  `${API_URL}/${assetType}/${asset.id}/previewPicture?quality=thumb`
                }
                color={asset.hex && asset.hex}
                handleClick={() => clickHandler(asset)}
                selected={asset.selected}
              />
            </Grid>
          );
        })}
      </Grid>
      <Box
        sx={{
          marginTop: 3,
          marginBottom: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {loading && <CircularProgress color="primary" />}
      </Box>
    </Box>
  );
}